import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Typography,
  Skeleton,
  Stack
} from '@mui/material';
import {
  Store as ShopifyIcon,
  Storage as ErpIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useConfigurationStatus } from '../../hooks/useConfigurationStatus';

const ConfigurationWarning = ({ children, showChildrenOnError = false, justShowContentWhenNoConfig = false }) => {
  const {
    hasActiveShopifyConfig,
    hasActiveErpConfig,
    isLoading,
    error
  } = useConfigurationStatus();

  if(justShowContentWhenNoConfig && (!hasActiveShopifyConfig || !hasActiveErpConfig)){
    return children;
  } else if(justShowContentWhenNoConfig && (hasActiveShopifyConfig && hasActiveErpConfig)){
    return null;
  } else if(justShowContentWhenNoConfig && !hasActiveShopifyConfig && !hasActiveErpConfig){
    return null;
  }

  // Show loading skeleton while checking configurations
  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Show error if there was an issue checking configurations
  if (error && !hasActiveShopifyConfig && !hasActiveErpConfig) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <AlertTitle>Configuration Check Failed</AlertTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Unable to verify your configuration settings. Please check your connection and try again.
        </Typography>
        {showChildrenOnError && children}
      </Alert>
    );
  }

  const missingConfigs = [];
  if (!hasActiveShopifyConfig) missingConfigs.push('Shopify');
  if (!hasActiveErpConfig) missingConfigs.push('ERP');

  // If both configurations are active, render children
  if (hasActiveShopifyConfig && hasActiveErpConfig) {
    return children;
  }

  // Show warning for missing configurations
  return (
    <Box>
      <Alert
        severity="warning"
        sx={{
          mb: 3,
          p: 3,
          border: '1px solid #ffa000',
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <AlertTitle sx={{ mb: 1 }}>Configuration Required</AlertTitle>
        <Typography variant="body2" sx={{ mb: 3 }}>
          {missingConfigs.length === 2
            ? 'Both Shopify and ERP configurations are required before you can use synchronization features.'
            : `Active ${missingConfigs[0]} configuration is required before you can use synchronization features.`
          }
        </Typography>

        <Stack direction="row" spacing={2} flexWrap="wrap">
          {!hasActiveShopifyConfig && (
            <Button
              component={Link}
              to="/shopify-config"
              variant="contained"
              size="medium"
              startIcon={<ShopifyIcon />}
              sx={{ minWidth: 'auto' }}
            >
              Configure Shopify
            </Button>
          )}
          {!hasActiveErpConfig && (
            <Button
              component={Link}
              to="/erp-config"
              variant="contained"
              size="medium"
              startIcon={<ErpIcon />}
              sx={{ minWidth: 'auto' }}
            >
              Configure ERP
            </Button>
          )}
        </Stack>
      </Alert>

      {/* Optionally show disabled content */}
      {showChildrenOnError && (
        <Box sx={{
          opacity: 0.5,
          pointerEvents: 'none',
          filter: 'grayscale(50%)'
        }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

export default ConfigurationWarning;
